"use client";

// ORGANISM CALM BUS — the one signal that tells every mounted organism "a frost
// is settling over you; rest". Readers and modals are the emitters: their scrims
// already ride a 0..1 ramp (--art-ramp / --os-ramp), and the SAME value feeds
// this bus, so the organism's render cost winds down exactly as fast as the
// frost covers it — and winds back up the moment it lifts.
//
// Sources are keyed by id and the published level is the MAX of all live
// sources, so a sheet closing over a still-open reader can never snap the bloom
// back to full power. MemoryOrganism listens for the event unconditionally —
// dynamic rendering is baked into the organism; surfaces only declare their
// frost. The engine then eases fps + backing resolution + its hottest shader
// loops down together (see FutureOrganism.setCalm), which ALSO collapses the
// scrims' backdrop-filter cost: a blur only re-runs when its backdrop repaints.

import { useEffect, useId } from "react";

export const CALM_EVENT = "innernet:organism-calm";

const sources = new Map<string, number>();
let lastSent = 0;

// LIFTED — netti standing IN FRONT of every frost (the veiled guide window
// lifts it above its own veil, sharp and awake). Resting is only invisible
// because a settled blur swallows the small version; with nothing over the
// creature, a rest shows as exactly that: a fifth of the resolution and half
// the lace, upscaled into a pale, stair-edged disc (the washed-out white netti
// behind the import sheet opened from a guide). While anything holds a lift,
// the bus publishes 0 whatever the sources say, and the sources are kept, so
// the rest comes back the moment the lift ends.
const lifts = new Set<string>();

function maxLevel(): number {
  if (lifts.size) return 0;
  let max = 0;
  sources.forEach((v) => {
    if (v > max) max = v;
  });
  return max;
}

function publish(): void {
  if (typeof window === "undefined") return;
  // quantized so a rAF scroll writer doesn't fire hundreds of no-op events
  const level = Math.round(maxLevel() * 200) / 200;
  if (level === lastSent) return;
  lastSent = level;
  try {
    window.dispatchEvent(new CustomEvent(CALM_EVENT, { detail: { level } }));
  } catch {
    /* never let a listener error break a scroll frame */
  }
}

/** The level a mid-frost mount should start at (a reader was already open). */
export function currentOrganismCalm(): number {
  return maxLevel();
}

/** Imperative source — for scroll writers that already run in a rAF.
 *  Pass null to release the source entirely (unmount). */
export function setOrganismCalm(id: string, level: number | null): void {
  if (level == null) sources.delete(id);
  else sources.set(id, Math.min(1, Math.max(0, level)));
  publish();
}

/** Hold netti at full life while it stands in front of every frost (see
 *  `lifts`). Pass false to let go. */
export function setOrganismLifted(id: string, lifted: boolean): void {
  if (lifted) lifts.add(id);
  else lifts.delete(id);
  publish();
}

/** Declarative source — an overlay (sheet/modal) whose backdrop frosts the
 *  organism while mounted. `active` may be a boolean (full frost while open)
 *  or a live 0..1 level. */
export function useOrganismCalm(active: boolean | number, level = 1): void {
  const id = useId();
  const lv = typeof active === "number" ? active : active ? level : 0;
  useEffect(() => {
    setOrganismCalm(id, lv);
    return () => setOrganismCalm(id, null);
  }, [id, lv]);
}
